import types from '@/actions/types';
import useSequence from '@/composables/useSequence';
import useConfig from '@/composables/useConfig';
import useExternalProjects from '@/composables/useExternalProjects';
import Base from '@/actions/base';
import useAnalytics from '@/composables/useAnalytics';
import { START_FLOW_ID } from '@/js/constants';

const previousPaths = {};

export default class RandomFlow extends Base {
  constructor(params) {
    super(params, types.RANDOM_FLOW);
  }

  execute() {
    const { runSequence } = useSequence();
    const { trackAction } = useAnalytics();
    const { setFlowStateBackToStart, getVariable, setActiveAvatar, pushReturnStack } = useConfig();
    const { hasProject, loadProject, processExternalConfig } = useExternalProjects();

    function switchProject(token, next, resolve) {
      pushReturnStack(next);
      setActiveAvatar(token);
      runSequence(START_FLOW_ID).then(resolve);
    }

    trackAction(this);

    return new Promise((resolve) => {
      this.resolve = resolve;
      const { flows, sequenceId, variables, projects, next } = this.params;

      if (projects) {
        const targetProject = projects[Math.floor((Math.random() * projects.length))];
        if (!hasProject(targetProject)) {
          loadProject(targetProject)
            .then(([token, newExternals]) => {
              if (token) {
                switchProject(targetProject, next, resolve);
                processExternalConfig(newExternals);
              } else {
                console.log(`Unable to load project: ${targetProject}`);
              }
            })
            .catch((err) => {
              console.log(err);
            });
        } else {
          switchProject(targetProject, next, resolve);
        }
        return;
      }

      const flowList = flows || variables
        .map((v) => getVariable(v).value)
        .filter((v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v));

      if (flowList.length === 1) {
        if (flowList[0] === START_FLOW_ID) {
          setActiveAvatar();
        }
        setFlowStateBackToStart(flowList[0]);
        runSequence(flowList[0]).then(resolve);
        return;
      }

      if (!previousPaths[sequenceId]) {
        previousPaths[sequenceId] = [];
      }

      let lastFlowId;
      if (previousPaths[sequenceId].length === flowList.length) {
        lastFlowId = previousPaths[sequenceId].pop();
        previousPaths[sequenceId] = [];
      }

      const availablePaths = flowList.filter((flowId) => !previousPaths[sequenceId].includes(flowId)
        && (!lastFlowId || lastFlowId !== flowId));

      const randomId = availablePaths[Math.floor((Math.random() * availablePaths.length))];
      previousPaths[sequenceId].push(randomId);

      if (randomId === START_FLOW_ID) {
        setActiveAvatar();
      }
      setFlowStateBackToStart(randomId);
      runSequence(randomId).then(resolve);
    });
  }

  stop() {
    if (this.resolve) {
      this.resolve();
    }
  }
}
